import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, 
  Box, CircularProgress, IconButton, InputAdornment, Alert,
  Typography, Paper, Chip
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import { useQuery } from 'react-query';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { getLocationByPostcode, getAddressDetailsById } from '../../api/services/geoFencing';
import CustomInput from '../FormFields/CustomInput/CustomInput';
import CustomSelect from '../FormFields/CustomSelect/CustomSelect';

/* ---------- CARTO API KEY ---------- */
const CARTO_KEY = process.env.REACT_APP_CARTO_MAP_KEY;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

function ChangeView({ center }) {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] && center[1] && map) {
      map.setView(center, map.getZoom(), {
        animate: true,
        duration: 0.5
      });
    }
  }, [center, map]);

  return null;
}

function MapEventsHandler({ onLocationChange }) {
  const map = useMap();
  
  useMapEvents({
    click(e) {
      if (e && e.latlng) {
        onLocationChange(e.latlng.lat, e.latlng.lng);
        map.setView(e.latlng, map.getZoom(), {
          animate: true,
          duration: 0.3
        });
      }
    },
  });
  return null;
}

function CustomZoomControls() {
  const map = useMap();
  
  const handleZoomIn = () => {
    if (map) {
      const currentZoom = map.getZoom();
      map.setZoom(currentZoom + 1, { animate: true });
    }
  };
  
  const handleZoomOut = () => {
    if (map) {
      const currentZoom = map.getZoom();
      map.setZoom(currentZoom - 1, { animate: true });
    }
  };
  
  return (
    <Box sx={{
      position: 'absolute',
      right: 16,
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      bgcolor: '#FFFFFF',
      borderRadius: 2,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      border: '1px solid #E2E8F0',
      overflow: 'hidden'
    }}>
      <IconButton 
        onClick={handleZoomIn}
        size="small"
        sx={{ 
          borderRadius: 0,
          p: 1.5,
          '&:hover': { bgcolor: '#F1F5F9' }
        }}
      >
        <ZoomInIcon fontSize="small" />
      </IconButton>
      <Box sx={{ 
        width: '100%', 
        height: 1, 
        bgcolor: '#E2E8F0' 
      }} />
      <IconButton 
        onClick={handleZoomOut}
        size="small"
        sx={{ 
          borderRadius: 0,
          p: 1.5,
          '&:hover': { bgcolor: '#F1F5F9' }
        }}
      >
        <ZoomOutIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const MapAddressPicker = ({ open, onClose, onLocationSelect, initialData }) => {
  const [postcodeInput, setPostcodeInput] = useState('');
  const [addressList, setAddressList] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState('');
  const [isRetrieving, setIsRetrieving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [noAddressFound, setNoAddressFound] = useState(false);
  const [shouldUpdateView, setShouldUpdateView] = useState(false);
  const markerRef = useRef(null);
  const mapRef = useRef(null);
  
  const [addressData, setAddressData] = useState({
    line1: initialData?.address_1 || '',
    line2: initialData?.address_2 || '',
    town: initialData?.town || '',
    county: initialData?.county || '',
    postcode: initialData?.postcode || '',
    lat: initialData?.lat || 51.5072, 
    lng: initialData?.lng || -0.1276
  });

  useEffect(() => {
    if (initialData && open) {
      setAddressData({
        line1: initialData.address_1 || '',
        line2: initialData.address_2 || '',
        town: initialData.town || '',
        county: initialData.county || '',
        postcode: initialData.postcode || '',
        lat: initialData.lat || 51.5072,
        lng: initialData.lng || -0.1276
      });
      if (initialData.postcode) {
        setPostcodeInput(initialData.postcode);
      }
      setShouldUpdateView(true);
    }
  }, [initialData, open]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current = null;
      }
    };
  }, []);

  const handleLocationUpdate = (lat, lng) => {
    setAddressData(prev => ({ ...prev, lat, lng }));
    setShouldUpdateView(true);
  };

  const eventHandlers = useMemo(
    () => ({
      dragend(e) {
        const marker = e.target;
        if (marker) {
          const { lat, lng } = marker.getLatLng();
          setAddressData(prev => ({ ...prev, lat, lng }));
          setShouldUpdateView(true);
        }
      },
    }),
    []
  );

  const handleSave = () => {
    if (onLocationSelect) {
      onLocationSelect(addressData);
    }
    onClose();
  };

  const { isLoading, refetch } = useQuery({
    queryKey: ['location', postcodeInput],
    queryFn: () => getLocationByPostcode(postcodeInput),
    enabled: false,
    retry: false,
    onSuccess: (data) => {
      setNoAddressFound(false);
      if (data && data.length > 0) {
        setAddressList(data);
        setSelectedIndex('');
        setErrorMessage('');
        setNoAddressFound(false);
      } else if (data && data.length === 0) {
        setNoAddressFound(true);
        setErrorMessage(`No addresses found for "${postcodeInput}"`);
        setAddressList([]);
      }
    },
    onError: (error) => {
      setNoAddressFound(false);
      if (error.response?.data?.error) {
        setErrorMessage(error.response.data.error);
      } else if (error.response?.data?.detail) {
        setErrorMessage(error.response.data.detail);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to fetch address. Please try again.');
      }
      setAddressList([]);
    },
  });

  const handleAddressChange = async (e) => {
    const index = e.target.value;
    setSelectedIndex(index);
    const selectedItem = addressList[index];

    if (selectedItem?.id) {
      setIsRetrieving(true);
      setErrorMessage('');
      setNoAddressFound(false);
      try {
        const details = await getAddressDetailsById(selectedItem.id);
        const updatedData = {
          line1: details.addressLine1 || '',
          line2: details.addressLine2 || '',
          town: details.town || '',
          county: details.county || '',
          postcode: details.postcode || postcodeInput || '',
          lat: parseFloat(details.geoLat) || 51.5072,
          lng: parseFloat(details.geoLong) || -0.1276
        };
        setAddressData(updatedData);
        setShouldUpdateView(true);
      } catch (err) {
        if (err.response?.data?.error) {
          setErrorMessage(err.response.data.error);
        } else if (err.response?.data?.detail) {
          setErrorMessage(err.response.data.detail);
        } else if (err.message) {
          setErrorMessage(err.message);
        } else {
          setErrorMessage('Failed to fetch address details');
        }
      } finally {
        setIsRetrieving(false);
      }
    }
  };

  const handleSearch = () => {
    const trimmedPostcode = postcodeInput.trim();
    if (!trimmedPostcode) {
      setErrorMessage('Please enter a postcode');
      setNoAddressFound(false);
      return;
    }
    
    setSelectedIndex('');
    setAddressList([]);
    setErrorMessage('');
    setNoAddressFound(false);
    refetch();
  };

  const handleClose = () => {
    setPostcodeInput('');
    setAddressList([]);
    setSelectedIndex('');
    setErrorMessage('');
    setNoAddressFound(false);
    setShouldUpdateView(false);
    onClose();
  };

  const addressOptions = addressList.map((option, index) => ({
    value: index,
    label: option.formatted_address
  }));

  const hasAddressSelected = selectedIndex !== '' && selectedIndex !== null;

  const mapKey = useMemo(() => 
    `${addressData.lat.toFixed(6)}-${addressData.lng.toFixed(6)}-${open}`,
    [addressData.lat, addressData.lng, open]
  );

  /* ---------- CARTO tile URL with API key ----------
   * NOTE: CARTO basemaps expect `?key=...` (NOT `?api_key=...`).
   * Docs: https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=YOUR_KEY
   */
  const cartoTileUrl = CARTO_KEY
    ? `https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`
    : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png';

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="lg"
      PaperProps={{ 
        sx: { 
          borderRadius: 3,
          overflow: 'hidden'
        } 
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        py: 2,
        px: 3,
        bgcolor: '#F8FAFC',
        borderBottom: '1px solid #E2E8F0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ 
            bgcolor: '#EEF2FF', 
            p: 1, 
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <LocationOnIcon sx={{ color: '#4F46E5', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0F172A', lineHeight: 1.2 }}>
              {initialData?.address_1 ? 'Edit Address Location' : 'Add Address Location'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              {initialData?.address_1 ? 'Update the address and pin location' : 'Search and confirm your address on map'}
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="small" 
          sx={{ 
            p: 0.5,
            bgcolor: '#F1F5F9',
            '&:hover': { bgcolor: '#E2E8F0' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
        {errorMessage && (
          <Alert 
            severity={noAddressFound ? "warning" : "error"}
            icon={noAddressFound ? <ErrorOutlineIcon /> : undefined}
            sx={{ 
              mb: 2.5, 
              borderRadius: 2,
              border: '1px solid',
              borderColor: noAddressFound ? '#FDE68A' : '#FCA5A5',
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
            onClose={() => {
              setErrorMessage('');
              setNoAddressFound(false);
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {noAddressFound ? 'No Addresses Found' : 'Error'}
              </Typography>
              <Typography variant="body2">
                {errorMessage}
              </Typography>
              {noAddressFound && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label="Check postcode format" 
                    size="small" 
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                  <Chip 
                    label="Try different postcode" 
                    size="small" 
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                  <Chip 
                    label="Manual entry below" 
                    size="small" 
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                </Box>
              )}
            </Box>
          </Alert>
        )}

        <Paper 
          elevation={0}
          sx={{ 
            p: 2.5,
            mb: 3,
            bgcolor: '#F8FAFC',
            borderRadius: 2,
            border: '1px solid #E2E8F0'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2,
            alignItems: { md: 'flex-end' }
          }}>
            <Box sx={{ flex: 1, width: '100%' }}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, mb: 0.5, display: 'block' }}>
                Postcode Lookup
              </Typography>
              <CustomInput
                placeholder="Enter postcode e.g., SW1A 1AA"
                value={postcodeInput}
                onChange={(e) => {
                  setPostcodeInput(e.target.value.toUpperCase());
                  if (errorMessage) {
                    setErrorMessage('');
                    setNoAddressFound(false);
                  }
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                error={!!errorMessage && !noAddressFound}
                helperText={!noAddressFound && errorMessage ? errorMessage : ''}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MyLocationIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {isLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Button 
                          variant="contained" 
                          onClick={handleSearch}
                          size="small"
                          sx={{ 
                            borderRadius: 1.5,
                            textTransform: 'none',
                            px: 2.5,
                            bgcolor: '#4F46E5',
                            '&:hover': { bgcolor: '#4338CA' }
                          }}
                        >
                          <SearchIcon sx={{ mr: 0.5, fontSize: 18 }} />
                          Search
                        </Button>
                      )}
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Box>

            <Box sx={{ flex: 1.5, width: '100%' }}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, mb: 0.5, display: 'block' }}>
                Select Address
              </Typography>
              <CustomSelect
                name="addressSelect"
                options={addressOptions}
                selectedValue={selectedIndex}
                onChange={handleAddressChange}
                placeholder={addressList.length === 0 ? "No addresses found" : "Choose from results"}
                disabled={addressList.length === 0 || isRetrieving}
                fullWidth
                size="small"
                initialLabel={false}
                labelKey="label"
                valueKey="value"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#FFFFFF'
                  }
                }}
              />
            </Box>
          </Box>

          {hasAddressSelected && (
            <Box sx={{ 
              mt: 2,
              pt: 2,
              borderTop: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <CheckCircleIcon sx={{ color: '#22C55E', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 500 }}>
                Address selected: 
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', noWrap: true }}>
                {addressList[selectedIndex]?.formatted_address}
              </Typography>
            </Box>
          )}
        </Paper>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 2.5,
            mb: 3,
            bgcolor: '#FFFFFF', 
            borderRadius: 2,
            border: '1px solid #E2E8F0'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0F172A' }}>
              Address Details
            </Typography>
            <Chip 
              label="Manual edit allowed" 
              size="small"
              variant="outlined"
              sx={{ 
                borderRadius: 1.5,
                borderColor: '#E2E8F0',
                color: '#64748B',
                fontSize: '0.65rem'
              }}
            />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row',
            gap: 2,
            flexWrap: 'nowrap',
            alignItems: 'flex-start'
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomInput
                label="Address Line 1"
                value={addressData.line1}
                onChange={(e) => setAddressData(prev => ({ ...prev, line1: e.target.value }))}
                fullWidth
                size="small"
                placeholder="e.g., 7 Hutton Road"
              />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomInput
                label="Address Line 2"
                value={addressData.line2}
                onChange={(e) => setAddressData(prev => ({ ...prev, line2: e.target.value }))}
                fullWidth
                size="small"
                placeholder="e.g., Flat 2"
              />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomInput
                label="Town / City"
                value={addressData.town}
                onChange={(e) => setAddressData(prev => ({ ...prev, town: e.target.value }))}
                fullWidth
                size="small"
                placeholder="e.g., Birmingham"
              />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomInput
                label="County / Region"
                value={addressData.county}
                onChange={(e) => setAddressData(prev => ({ ...prev, county: e.target.value }))}
                fullWidth
                size="small"
                placeholder="e.g., West Midlands"
              />
            </Box>
          </Box>
        </Paper>

        <Box sx={{ 
          borderRadius: 2, 
          overflow: 'hidden', 
          border: '1px solid #E2E8F0',
          position: 'relative',
          height: 440,
          bgcolor: '#F8FAFC'
        }}>
          <Box sx={{ 
            position: 'absolute', 
            top: 12, 
            left: 12, 
            zIndex: 1000,
            bgcolor: '#FFFFFF',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
              📍 Drag marker • +/- Zoom
            </Typography>
            <Box sx={{ 
              width: 4, 
              height: 4, 
              borderRadius: '50%', 
              bgcolor: '#22C55E',
              display: 'inline-block'
            }} />
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Click on map to reposition
            </Typography>
          </Box>
          
          {open && (
            <MapContainer 
              key={mapKey}
              center={[addressData.lat, addressData.lng]} 
              zoom={18}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false}
              maxZoom={20}
              minZoom={15}
              zoomSnap={0.5}
              zoomDelta={0.5}
              wheelPxPerZoomLevel={120}
              whenCreated={(map) => {
                mapRef.current = map;
                map.scrollWheelZoom.enable();
              }}
            >
              <TileLayer 
                url={cartoTileUrl}
                attribution={
                  CARTO_KEY
                    ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
                }
                subdomains="abcd"
                maxZoom={20}
                minZoom={10}
              />
              <ChangeView center={[addressData.lat, addressData.lng]} />
              <MapEventsHandler onLocationChange={handleLocationUpdate} />
              <Marker 
                position={[addressData.lat, addressData.lng]} 
                draggable={true} 
                eventHandlers={eventHandlers}
                ref={markerRef}
              />
              <Circle 
                center={[addressData.lat, addressData.lng]} 
                radius={50} 
                pathOptions={{ 
                  color: '#4F46E5', 
                  fillColor: '#4F46E5', 
                  fillOpacity: 0.1,
                  weight: 2,
                  dashArray: '5, 5'
                }} 
              />
              <CustomZoomControls />
            </MapContainer>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 2.5,
        gap: 1.5,
        borderTop: '1px solid #E2E8F0',
        bgcolor: '#F8FAFC'
      }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          sx={{ 
            borderRadius: 2, 
            textTransform: 'none',
            px: 4,
            py: 1,
            borderColor: '#E2E8F0',
            color: '#64748B',
            '&:hover': { 
              borderColor: '#94A3B8',
              bgcolor: '#F1F5F9'
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          sx={{ 
            borderRadius: 2, 
            textTransform: 'none',
            px: 4,
            py: 1,
            bgcolor: '#4F46E5',
            '&:hover': { bgcolor: '#4338CA' },
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)'
          }}
          startIcon={<CheckCircleIcon />}
        >
          Confirm & Save Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapAddressPicker;